/**
 * A website widget's allowed web origins — plan-004 WC-40, design §17.6.3 (decision D2 (a)).
 *
 * ONE IMPLEMENTATION, used by the api (the Messaging page's origins editor, the widget voice grant)
 * and by the runtime (the chat socket upgrade, the settings preflight, the voice frame's
 * `frame-ancestors`). Two spellings of "the same origin" would let a listed site be refused, or an
 * unlisted one be answered, depending on which process asked.
 *
 * `channel_binding_origin.origin` stores exactly what `normaliseOrigin` returns, and migration 0036's
 * CHECK is the database's half of the same rule: `^https?://[a-z0-9.-]+(:[0-9]{1,5})?$`, at most 262
 * characters. A value this function returns always satisfies that CHECK; a value it refuses is never
 * written.
 *
 * AN ORIGIN CHECK IS NOT AUTHENTICATION. It stops a widget being embedded on another site, because a
 * browser sends an honest `Origin`; a non-browser client can send any header. The per-address limits,
 * the proof of work and admission are the abuse bounds (design §17.6.3, §17.13).
 */
/** Migration 0036's CHECK on `channel_binding_origin.origin`, restated for the application's half. */
export const WEB_ORIGIN_PATTERN = /^https?:\/\/[a-z0-9.-]+(:[0-9]{1,5})?$/;
/** Migration 0036's length bound on `channel_binding_origin.origin`. */
export const WEB_ORIGIN_MAX_LENGTH = 262;
/**
 * The hosts `http:` is accepted for: a local test page is a secure context only on these, so they are
 * the only plain-text origins a widget can usefully be embedded on. Every other host needs `https:`.
 */
const PLAIN_TEXT_HOST = new Set(["localhost", "127.0.0.1"]);
/**
 * `input` as `scheme://host[:port]`, lower-case, with the default port dropped — or `null` when it is
 * not an origin a widget may be listed on.
 *
 * Refused (each → `null`): anything the URL parser cannot read; a scheme other than `https:` (or
 * `http:` on `localhost` / `127.0.0.1`); a username or password; a query or fragment, even an empty
 * one; a path other than `/`; an IPv6 literal; a host the table's CHECK does not accept (for example
 * one with an underscore); anything over 262 characters.
 */
export function normaliseOrigin(input) {
    const trimmed = input.trim();
    // An empty `?` or `#` leaves `search` / `hash` empty after parsing, so the raw text is checked too.
    if (trimmed.includes("?") || trimmed.includes("#"))
        return null;
    let parsed;
    try {
        parsed = new URL(trimmed);
    }
    catch {
        return null;
    }
    // The parser has already lower-cased the host, punycoded an IDN and dropped a default port.
    const host = parsed.hostname.toLowerCase();
    if (parsed.protocol === "http:") {
        if (!PLAIN_TEXT_HOST.has(host))
            return null;
    }
    else if (parsed.protocol !== "https:") {
        return null;
    }
    if (parsed.username !== "" || parsed.password !== "")
        return null;
    if (parsed.search !== "" || parsed.hash !== "")
        return null;
    if (parsed.pathname !== "" && parsed.pathname !== "/")
        return null;
    if (host.startsWith("["))
        return null;
    const scheme = parsed.protocol === "https:" ? "https" : "http";
    const origin = parsed.port === "" ? `${scheme}://${host}` : `${scheme}://${host}:${parsed.port}`;
    if (origin.length > WEB_ORIGIN_MAX_LENGTH || !WEB_ORIGIN_PATTERN.test(origin))
        return null;
    return origin;
}
//# sourceMappingURL=web-origin.js.map